import { inject, Injectable } from "@angular/core";
import { LiveUpdateService } from "./live-update.service";
import { BetPlacementService } from "./bet-placement.service";
import { Bet, CalculationBet, Market, MarketProvider, PredictedBet, Runner } from "../types/bet.type";
import { isNumeric, safeParseFloat } from "../utils/common";
import { ApiService } from "./api.service";
import { LoaderService } from "./loader.service";
import { BalanceService } from "./balance.service";
import { BetService } from "./bet.service";

@Injectable({
  providedIn: "root",
})
export class CashOutService {
  apiService = inject(ApiService);
  placementService = inject(BetPlacementService);
  liveUpdateService = inject(LiveUpdateService);
  loader = inject(LoaderService);
  balanceService = inject(BalanceService);
  market: Market | null = null;
  betService = inject(BetService);

  constructor() {}

  isBalanceInsufficient(cashOutBets: any[], betsForMarket: Bet[]) {
    // First check if there are any unmatched bets in the market
    const hasUnmatchedBets = betsForMarket.some(bet => {
      const sizeRemaining = parseFloat(bet?.sizeRemaining?.toString() || '0');
      return sizeRemaining > 0;
    });
    
    if (hasUnmatchedBets) {
      return true;
    }
    
    const balance = this.balanceService.balanceData$.getValue();
    const cashOutBetsValidations: ({
      insufficientBalance: boolean;
    } | null)[] = [];

    if (balance && cashOutBets) {
      cashOutBets.forEach((bet, index, cashOutBets) => {
        // Generate a predicted bet for the current iteration cash out bet
        const predictedBet: PredictedBet = {
          marketGroupId: bet.MARKET_ID,
          marketId: bet.MARKET_ID,
          marketName: bet.MARKET_NAME,
          selectionId: bet.SELECTION_ID,
          side: bet.SIDE,
          fOdds: this.betService.getFinalOdds(bet.PRICE, MarketProvider.BF, false),
          stake: bet.SIZE,
          bettingType: bet.BETTING_TYPE,
          provider: MarketProvider.BF,
          handicap: 0,
          averagePriceMatched: bet.F_ODDS,
        };

        // Generate predicted bets for previous iteration's cash-out bets
        const previousCashOutBets: PredictedBet[] = cashOutBets.slice(0, index).map((cashOutBet) => ({
          marketGroupId: cashOutBet.MARKET_ID,
          marketId: cashOutBet.MARKET_ID,
          marketName: cashOutBet.MARKET_NAME,
          selectionId: cashOutBet.SELECTION_ID,
          side: cashOutBet.SIDE,
          fOdds: this.betService.getFinalOdds(cashOutBet.PRICE, MarketProvider.BF, false),
          stake: cashOutBet.SIZE,
          bettingType: cashOutBet.BETTING_TYPE,
          provider: MarketProvider.BF,
          handicap: 0,
          averagePriceMatched: bet.F_ODDS,
        }));

        // Combine current user orders with the previous cash-out bets
        const combinedBets: CalculationBet[] = [
          ...betsForMarket.map((bet) => ({
            ...bet,
            marketGroupId: this.betService.generateMarketGroupIdForCalculationBet(bet),
            rangeOdd: bet.averagePriceMatched ?? bet.dfOdds,
          })),
          ...previousCashOutBets.map((previousCashOutBet) => this.betService.enhancePredictedBetToCalculationBet(previousCashOutBet)),
        ];

        // Evaluate the exposure calculation validation for each iteration and push the result into a variable to be checked later
        // const validationResult = this.betService.evaluateExposureValidation(combinedBets, balance, predictedBet, "-1", false);
        // cashOutBetsValidations.push(validationResult);
      });
    }

    // Calculate if the button should be enabled or not
    return cashOutBetsValidations.some((validation) => validation !== null);
  }

  getRunnerOdds(updatedRunners: any) {
    const runnerOdds = new Map<number, any>();
    updatedRunners?.forEach((runner: any) => {
      const [bestBack] = runner?.ex?.abp || [];
      const [bestLay] = runner?.ex?.alp || [];
      runnerOdds.set(runner.id, { bestBack, bestLay });
    });
    return runnerOdds;
  }

  getMarketPnLs(marketBets: Bet[], runners: Runner[]) {
    const checkSameSelectionId = (): boolean => {
      if (marketBets.length === 0) return true;

      const [firstBet] = marketBets;
      return marketBets.every((bet) => bet?.selectionId === firstBet?.selectionId && bet?.side === firstBet?.side);
    };
    return { type: checkSameSelectionId() ? "single" : "multiple", data: this.getRunnerPnLs(marketBets, runners) };
  }

  getRunnerPnLs(bets: Bet[], runners: Runner[]) {
    const runnerPnLs = new Map<number, any>();
    runners?.forEach((runner) => {
      let PnL = 0;
      let isBetRunner = false;
      bets.forEach((bet) => {
        const isBack = bet?.side === "BACK";
        const isSameSelection = bet?.selectionId === runner.selectionId;
        if (isSameSelection) isBetRunner = true;
        const profit = safeParseFloat(bet?.potentialProfit);
        const loss = safeParseFloat(bet?.potentialLoss);

        if (isBack) {
          PnL += isSameSelection ? profit : -loss;
        } else {
          PnL += isSameSelection ? -loss : profit;
        }
      });
      runnerPnLs.set(runner.selectionId, { currentPnL: PnL, isBetRunner });
    });
    const minPnL = () => {
      const PnLs = Array.from(runnerPnLs.values(), (item) => item?.currentPnL);
      return { minPnL: Math.min(...PnLs) };
    };
    const singlePnLs = () => {
      const PnLs = Array.from(runnerPnLs.values());
      const betPnL = PnLs.find((pnl) => pnl?.isBetRunner)?.currentPnL;
      const nonBetPnL = PnLs.find((pnl) => !pnl?.isBetRunner)?.currentPnL;
      const [bet] = bets ?? [];
      return { betPnL, nonBetPnL, bet };
    };

    return { runnerPnLs, ...minPnL(), ...singlePnLs() };
  }

  generateOffer(market: Market, runnerUpdates: any[], marketBets: Bet[]) {
    // console.log("market", market);
    // console.log("runnerUpdates", runnerUpdates);
    // console.log("marketBets", marketBets);
    this.market = market;
    const { runners } = market || {};
    const marketUpdates = this.getRunnerOdds(runnerUpdates);
    const marketPnLs = this.getMarketPnLs(marketBets, runners);

    if (!marketPnLs || !marketUpdates) return;

    if (marketPnLs?.type === "multiple") {
      return this.multipleOffer(marketPnLs, market, marketUpdates);
    } else {
      return this.singleOffer(marketPnLs, market, marketUpdates);
    }
  }

  singleOffer(marketPnLs: any, market: any, marketUpdates: any) {
    const { marketId, marketName, description, event, eventId, bettingType, eventType, topic, serviceId, minBet, maxBet } = market || {};
    const minPnL = marketPnLs?.data?.minPnL;
    const PnLData = marketPnLs?.data;
    const betPnL = PnLData?.betPnL;
    const nonBetPnL = PnLData?.nonBetPnL;
    const PnLBet = PnLData?.bet;
    const bestPrices = marketUpdates?.get(PnLBet.selectionId);
    let baseBet = {
      ...(topic && { descriptionPrefix: "Bookmaker", topic, minBet, maxBet }),
      PROVIDER: topic ? "SB" : "BF",
      PROVIDER_ID: "0",
      SERVICE_ID: topic ? serviceId : "0",
      MARKET_ID: marketId,
      MARKET_NAME: marketName,
      SPORT_ID: eventType?.id,
      EVENT_ID: topic ? eventId : event?.id,
      PERSISTENCE_TYPE: "PERSIST",
      SELECTION_ID: PnLBet?.selectionId,
      HANDICAP: PnLBet?.handicap ?? 0,
      BETTING_TYPE: topic ? bettingType : description?.["bettingType"],
      runner: PnLBet.runnerName,
    };
    // console.log(betPnL, nonBetPnL);

    // const isBack = backCount > layCount;
    const isBack = betPnL > 0;
    const selectedOdds = isBack ? bestPrices?.bestLay : bestPrices?.bestBack;
    const difference = isBack ? betPnL - nonBetPnL : nonBetPnL - betPnL;
    const stake = difference / selectedOdds;
    const side = isBack ? "LAY" : "BACK";
    const profit = side === "LAY" ? betPnL - (selectedOdds - 1) * stake : betPnL + (selectedOdds - 1) * stake;
    const _stake = Math.abs(stake);
    baseBet = {
      ...baseBet,
      SIZE: _stake,
      SIDE: side,
      PRICE: selectedOdds,
      F_ODDS: selectedOdds,
    };

    if (_stake < 0.01 || !isNumeric(_stake)) return null;

    return {
      bets: [baseBet],
      profit,
      cashOut: _stake,
      minPnL,
    };
  }

  multipleOffer(marketPnLs: any, market: any, marketUpdates: any) {
    const { marketId, marketName, runners, description, event, eventId, bettingType, eventType, serviceId, topic, minBet, maxBet } = market || {};
    const minPnL = marketPnLs?.data?.minPnL;
    const runnersWithStake = runners?.map((runner: Runner) => {
      const bestPrices = marketUpdates?.get(runner.selectionId);
      const currentSelectionPnL = marketPnLs?.data?.runnerPnLs?.get(runner.selectionId)?.currentPnL;
      const PnLDifference = minPnL - currentSelectionPnL;
      const selectedOdd = PnLDifference < 0 ? (bestPrices?.bestLay ?? 0) : PnLDifference > 0 ? (bestPrices?.bestBack ?? 0) : 0;
      const stake = PnLDifference === 0 || selectedOdd === 0 ? 0 : PnLDifference / selectedOdd;
      const side = PnLDifference < 0 ? "LAY" : PnLDifference > 0 ? "BACK" : null;

      return {
        stake,
        selectedOdd,
        side,
        ...runner,
      };
    });

    const stakeSum = runnersWithStake?.filter((r: any) => typeof r?.stake === "number")?.reduce((sum: any, runner: any) => sum + runner.stake, 0);
    const profit = minPnL - stakeSum;
    const betsToBePlaced: any[] = [];
    // console.log("Profit =", minPnL, "-", stakeSum, "=", profit);

    runnersWithStake?.forEach((runner: any) => {
      const { selectedOdd, stake, selectionId, side, handicap } = runner;
      const _stake = Math.abs(stake);
      if (_stake > 0.01 && isNumeric(_stake)) {
        const bet = {
          ...(topic && { descriptionPrefix: "Bookmaker", topic, minBet, maxBet }),
          PROVIDER: topic ? "SB" : "BF",
          PROVIDER_ID: "0",
          SERVICE_ID: topic ? serviceId : "0",
          PRICE: runner?.selectedOdd,
          SIZE: _stake,
          SIDE: side,
          MARKET_ID: marketId,
          MARKET_NAME: marketName,
          SPORT_ID: eventType?.id,
          EVENT_ID: topic ? eventId : event?.id,
          PERSISTENCE_TYPE: "PERSIST",
          SELECTION_ID: selectionId,
          HANDICAP: handicap ?? 0,
          BETTING_TYPE: topic ? bettingType : description?.["bettingType"],
          F_ODDS: selectedOdd,
          runner: runner.runnerName,
        };
        betsToBePlaced.push(bet);
      }
    });

    // console.log(runnersWithStake);
    // console.log("Bets:", betsToBePlaced);

    // console.log({
    //   bets: betsToBePlaced,
    //   profit,
    //   cashOut: stakeSum,
    // });

    if (!betsToBePlaced?.length) return null;

    return {
      bets: betsToBePlaced,
      profit,
      cashOut: Math.abs(stakeSum),
      minPnL,
    };
  }
}
